export type ContextId = string;
export type ExtensionId = string;

export interface ExtensionInstanceIds {
	contextIds: ContextId[];
	extensionId: ExtensionId;
	functionId?: string;
	localId: string;
}

export enum ExtensionEnvironment {
	DEVELOPMENT = 'DEVELOPMENT',
	STAGING = 'STAGING',
	PRODUCTION = 'PRODUCTION',
}

export type EnvironmentType = keyof typeof ExtensionEnvironment;
export type ViewportSizeType = 'small' | 'medium' | 'large' | 'xlarge' | 'max';
export type LayoutType = 'inline' | 'block' | 'bodied';

type AutoConvertMatches = {
	pattern: string;
};

export interface ForgeUIExtensionType {
	appId?: string;
	appOwner?: {
		accountId: string;
		name: string;
		picture: string;
	};
	appVersion?: string;
	consentUrl?: string;
	currentUserConsent?: {
		appEnvironmentVersion: {
			id: string;
		};
		consentedAt: string;
		user: {
			aaid: string;
		};
	};
	dataClassificationPolicyDecision?: {
		status: string;
	};
	environmentId?: string;
	environmentKey?: string;
	environmentType?: EnvironmentType;
	id: string;
	installationId?: string;
	/** Module key */
	key?: string;
	migrationKey?: string;
	properties: {
		autoConvert?: { matchers: Array<AutoConvertMatches> };
		categories?: string[];
		config?: {
			// UI Kit 1
			function?: string;
			openOnInsert?: boolean;
			render?: 'default' | 'native';
			// UI Kit 2 / Custom UI
			resource?: string;
			title?: string;
			viewportSize?: ViewportSizeType;
		};
		description?: string;
		emitsReadyEvent?: boolean;
		icon?: string;
		layout?: LayoutType;
		render?: 'default' | 'native';
		resource?: string;
		title?: string;
		viewportSize?: ViewportSizeType;
	};
	requiresUserConsent?: boolean;
	type?: string; // e.g. confluence:contentAction, confluence:contextMenu
}

interface ADFEntityMark {
	attrs?: { [name: string]: any };
	type: string;
}

interface ADFEntity {
	[key: string]: any;
	attrs?: { [name: string]: any };
	content?: Array<ADFEntity | undefined>;
	marks?: Array<ADFEntityMark>;
	text?: string;
	type: string;
}

// duplicated ReferenceEntity from @atlaskit/editor-common to avoid adding @atlaskit/editor-common as a dependency
export type ReferenceEntity = {
	[prop: string]: ADFEntity | Object;
};

export const ConfluenceExtension = {
	BYLINE: 'confluence:contentBylineItem',
	CONTEXT_MENU: 'confluence:contextMenu',
	CONTENT_ACTION: 'confluence:contentAction',
	GLOBAL_PAGE: 'confluence:globalPage',
	GLOBAL_SETTINGS: 'confluence:globalSettings',
	HOMEPAGE_FEED: 'confluence:homepageFeed',
	SPACE_PAGE: 'confluence:spacePage',
	SPACE_SETTINGS: 'confluence:spaceSettings',
	CUSTOM_CONTENT: 'confluence:customContent',
	MACRO: 'xen:macro',
	TEST_MODULE: 'test:module',
} as const;

export type ConfluenceExtensionType =
	(typeof ConfluenceExtension)[keyof typeof ConfluenceExtension];

export const JiraExtension = {
	BACKLOG_ACTION: 'jira:backlogAction',
	BOARD_ACTION: 'jira:boardAction',
	PERSONAL_SETTINGS_PAGE: 'jira:personalSettingsPage',
	ISSUE_PANEL: 'jira:issuePanel',
	ISSUE_ACTION: 'jira:issueAction',
	ISSUE_GLANCE: 'jira:issueGlance',
	ISSUE_CONTEXT: 'jira:issueContext',
	ISSUE_ACTIVITY: 'jira:issueActivity',
	UI_MODIFICATIONS: 'jira:uiModifications',
	CUSTOM_FIELD: 'jira:customField',
	CUSTOM_FIELD_TYPE: 'jira:customFieldType',
	ADMIN_PAGE: 'jira:adminPage',
	GLOBAL_PAGE: 'jira:globalPage',
	PROJECT_PAGE: 'jira:projectPage',
	PROJECT_SETTINGS_PAGE: 'jira:projectSettingsPage',
	DASHBOARD_BACKGROUND_SCRIPT: 'jira:dashboardBackgroundScript',
	ISSUE_VIEW_BACKGROUND_SCRIPT: 'jira:issueViewBackgroundScript',
	DASHBOARD_GADGET: 'jira:dashboardGadget',
	WORKFLOW_VALIDATOR: 'jira:workflowValidator',
	WORKFLOW_CONDITION: 'jira:workflowCondition',
	WORKFLOW_POST_FUNCTION: 'jira:workflowPostFunction',
	SERVICE_DESK_QUEUE_PAGE: 'jiraServiceManagement:queuePage',
	SERVICE_DESK_ORGANIZATION_PANEL: 'jiraServiceManagement:organizationPanel',
} as const;

export type JiraExtensionType = (typeof JiraExtension)[keyof typeof JiraExtension];

export type ForgeExtensionPoints = JiraExtensionType | ConfluenceExtensionType;
